// 11 §13.2 / A5 — typed access to the Electron preload bridge. In the browser `window.fellowkeep`
// is absent and every call is a no-op (or resolves to a sensible default), so nothing in the
// client depends on Node or Electron except through this file.
//
// The bridge's type is the desktop shell's own (`apps/desktop/src/shared/bridge.d.ts`, the one the
// preload implements); this file adapts it to what the client's stores want, so a mismatch between
// the two shows up as a type error here instead of a desktop build that silently loses settings.
import type { FellowkeepDesktop } from '../../../desktop/src/shared/bridge';

export type { FellowkeepDesktop };

export interface FellowkeepBridge {
  readonly platform: 'linux' | 'win32' | 'darwin';
  readonly version: string;
  auth: {
    getRefreshToken(): Promise<string | null>;
    setRefreshToken(token: string | null): Promise<void>;
  };
  window: {
    setFullscreen(on: boolean): Promise<void>;
    isFullscreen(): Promise<boolean>;
    onFullscreenChanged(cb: (on: boolean) => void): () => void;
  };
  settings: {
    /** The local copy as JSON text (localStorage in the browser, `userData/settings.json` on desktop). */
    read(): Promise<string | null>;
    write(json: string): Promise<void>;
  };
}

export const isDesktop = (): boolean => typeof window !== 'undefined' && !!window.fellowkeep;

/** The raw preload bridge (desktop only): auth, Steam, updates. */
export const desktopBridge = (): FellowkeepDesktop | undefined => (typeof window !== 'undefined' ? window.fellowkeep : undefined);

const noopBridge: FellowkeepBridge = {
  platform: 'linux',
  version: 'browser',
  auth: {
    // (storage can throw outright in a private window or a sandboxed frame: none is no token)
    getRefreshToken: async () => { try { return sessionStorage.getItem('fk.refresh'); } catch { return null; } },
    setRefreshToken: async (t) => {
      try { if (t) sessionStorage.setItem('fk.refresh', t); else sessionStorage.removeItem('fk.refresh'); } catch { /* none: signed in for this page only */ }
    },
  },
  window: { setFullscreen: async () => undefined, isFullscreen: async () => false, onFullscreenChanged: () => () => undefined },
  settings: {
    read: async () => { try { return localStorage.getItem('fk.settings'); } catch { return null; } },
    write: async (json) => { try { localStorage.setItem('fk.settings', json); } catch { /* private mode or full: the settings still apply */ } },
  },
};

/** The client's view of a real bridge: settings go over as objects, the refresh token as a safeStorage secret. */
function adapt(d: FellowkeepDesktop): FellowkeepBridge {
  return {
    platform: d.platform,
    version: d.appVersion,
    auth: {
      getRefreshToken: () => d.settings.readSecret('refresh_token'),
      setRefreshToken: (t) => d.settings.writeSecret('refresh_token', t),
    },
    window: d.window,
    settings: {
      read: async () => {
        const s = await d.settings.read().catch(() => null);
        return s ? JSON.stringify(s) : null;
      },
      // a refused write (main's size cap) keeps the old file; the in-memory settings still apply
      write: (json) => d.settings.write(JSON.parse(json) as Record<string, unknown>).catch((err: unknown) => console.warn('[desktop] settings not saved', err)),
    },
  };
}

let adapted: FellowkeepBridge | undefined;

/** The bridge if present, otherwise the browser no-op implementation. */
export function desktop(): FellowkeepBridge {
  const d = desktopBridge();
  if (!d) return noopBridge;
  return (adapted ??= adapt(d));
}
